import React, { useState } from 'react';
import { ethers } from 'ethers';
import { UserIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/solid';

const RecipientInput = ({ 
  recipient, 
  onRecipientChange, 
  currentAddress, 
  disabled = false,
  toChainName 
}) => {
  const [isValidating, setIsValidating] = useState(false);
  const [validationStatus, setValidationStatus] = useState(null); // 'valid', 'invalid', null

  const validateAddress = async (address) => {
    if (!address) {
      setValidationStatus(null);
      return;
    }

    setIsValidating(true);
    try {
      // Check if it's a valid Ethereum address
      const isValid = ethers.utils.isAddress(address);
      setValidationStatus(isValid ? 'valid' : 'invalid');
    } catch (error) {
      setValidationStatus('invalid');
    } finally {
      setIsValidating(false);
    }
  };

  const handleAddressChange = (e) => {
    const value = e.target.value.trim();
    onRecipientChange(value);
    
    // Debounced validation
    if (value) {
      setTimeout(() => validateAddress(value), 300);
    } else {
      setValidationStatus(null);
    }
  };

  const handleUseSameAddress = () => {
    onRecipientChange(currentAddress);
    setValidationStatus('valid');
  };

  const getInputClassNames = () => {
    let baseClasses = "input-field pr-10";
    
    if (disabled) {
      baseClasses += " opacity-50 cursor-not-allowed";
    } else if (validationStatus === 'valid') {
      baseClasses += " border-success-300 focus:border-success-500 focus:ring-success-500";
    } else if (validationStatus === 'invalid') {
      baseClasses += " border-error-300 focus:border-error-500 focus:ring-error-500";
    }
    
    return baseClasses;
  };

  const getStatusIcon = () => {
    if (isValidating) {
      return <ArrowPathIcon className="w-5 h-5 text-slate-400 animate-spin" />;
    }
    
    if (validationStatus === 'valid') {
      return <CheckCircleIcon className="w-5 h-5 text-success-500" />;
    }
    
    if (validationStatus === 'invalid') {
      return <ExclamationTriangleIcon className="w-5 h-5 text-error-500" />;
    }
    
    return null;
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
          Recipient Address ({toChainName})
        </label>
        {currentAddress && (
          <button
            type="button"
            onClick={handleUseSameAddress}
            disabled={disabled}
            className="text-xs text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-200 disabled:opacity-50"
          >
            Use same address
          </button>
        )}
      </div>
      
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <UserIcon className="h-5 w-5 text-slate-400" />
        </div>
        
        <input
          type="text"
          value={recipient}
          onChange={handleAddressChange}
          disabled={disabled}
          placeholder={`Enter ${toChainName} address (0x...)`}
          className={getInputClassNames()}
          style={{ paddingLeft: '2.5rem' }}
        />
        
        {(getStatusIcon() || isValidating) && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
            {getStatusIcon()}
          </div>
        )}
      </div>
      
      {/* Validation Messages */}
      {validationStatus === 'invalid' && (
        <p className="text-xs text-error-600 dark:text-error-400">
          Invalid address format. Please enter a valid Ethereum address.
        </p>
      )}
      
      {validationStatus === 'valid' && recipient !== currentAddress && (
        <p className="text-xs text-warning-600 dark:text-warning-400">
          ⚠️ You're sending to a different address. Please double-check the recipient address.
        </p>
      )}
      
      {recipient === currentAddress && (
        <p className="text-xs text-success-600 dark:text-success-400">
          ✅ Sending to your own address
        </p>
      )}
      
      {!recipient && (
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Leave empty to send to your current address ({currentAddress ? `${currentAddress.slice(0, 6)}...${currentAddress.slice(-4)}` : 'Not connected'})
        </p>
      )}
    </div>
  );
};

export default RecipientInput;